import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { initialsOf } from "../lib/initials";
import { AvatarIcon } from "./avatar-icon";
import { Button, buttonVariants } from "./button";
import { Popover, PopoverItem } from "./popover";
import { Separator } from "./separator";
import { StatusBadge } from "./status-badge";
import { SunkenPanel } from "./sunken-panel";

export interface AccountMenuUser {
	name: string;
	/**
	 * Почта владельца аккаунта, ЦЕЛИКОМ.
	 *
	 * Инвариант §5 («полная почта не показывается») говорит про account
	 * chooser ДО входа: там адрес принадлежит ещё не подтверждённому человеку,
	 * и показывать его тому, кто открыл страницу, нельзя. Это меню человек
	 * открывает про СВОЙ аккаунт, уже войдя, — прятать от него его же адрес
	 * не защита, а загадка. Маскировка живёт в `sso-web` (`maskEmail`) и
	 * остаётся там.
	 */
	email: string;
	avatarUrl?: string;
	initials?: string;
}

export interface AccountMenuEntry {
	key: string;
	label: ReactNode;
	icon?: ReactNode;
	/** Либо `href`, либо `onSelect` — компонент не знает про роутер. */
	href?: string;
	onSelect?: () => void;
	trailing?: ReactNode;
	danger?: boolean;
}

export interface AccountMenuLabels {
	manage: string;
	signOut: string;
	pointsUnit: string;
	menu: string;
}

export interface AccountMenuProps {
	user: AccountMenuUser;
	/** Уже отформатированный баланс: форматирование локали живёт в приложении. */
	pointsBalance?: string;
	/** Уровень подписки: «IEC+», «IEC+ Pro». Пусто — бейдж не рисуется. */
	plan?: string;
	manageHref?: string;
	onManage?: () => void;
	/** Ярлыки в разделы витрины IEC ID. */
	items?: readonly AccountMenuEntry[];
	/** Название сервиса-хозяина над блоком его собственных пунктов. */
	serviceName?: string;
	serviceItems?: readonly AccountMenuEntry[];
	signOutHref?: string;
	onSignOut?: () => void;
	/**
	 * Все подписи меню. Объект обязателен целиком: меню встраивается в чужие
	 * сервисы, и русское умолчание кита уезжало бы в их английский интерфейс
	 * (аудит В1).
	 */
	labels: AccountMenuLabels;
	className?: string;
}

/**
 * Меню аккаунта IEC ID (§6.3): шапка, баланс Баллов с бейджем IEC+, кнопка
 * управления аккаунтом, ярлыки в разделы витрины, слот сервиса, выход.
 *
 * Компонент собран так, чтобы его можно было вставить в чужой сервис: ни
 * роутера, ни стора, ни i18n-контекста — весь текст и все переходы приходят
 * пропами. Единственная внешняя зависимость — токены из `@iec-id/ui/styles`.
 *
 * Ширина 320 из макета 3g. В 300 (вариант со state board) он тоже живёт —
 * ширину задаёт вставляющая сторона через className.
 */
export function AccountMenu({
	user,
	pointsBalance,
	plan,
	manageHref,
	onManage,
	items = [],
	serviceName,
	serviceItems = [],
	signOutHref,
	onSignOut,
	labels: text,
	className,
}: AccountMenuProps) {
	const hasService = serviceName != null || serviceItems.length > 0;

	return (
		<div
			className={cn(
				// 320px из макета 3g, но не шире экрана: меню встраивается в чужую
				// шапку и на 320-м iPhone фиксированные 320px + отступ поповера
				// вылезают за край вместе с кнопкой выхода.
				"flex w-[min(20rem,calc(100vw-1rem))] flex-col gap-3.5 rounded-card bg-card p-4 shadow-float",
				// Высота — сколько осталось до края экрана. Переменную ставит
				// поповер-обёртка; вне поповера она не определена, и потолок просто
				// не применяется. Скролл живёт здесь, а не на обёртке: иначе
				// `overflow` срезал бы этой же карточке её тень.
				"iec-scroll max-h-[var(--radix-popover-content-available-height)]",
				className,
			)}
		>
			<div className="flex items-center gap-3">
				<AvatarIcon
					size={48}
					{...(user.avatarUrl !== undefined ? { src: user.avatarUrl } : {})}
					initials={user.initials ?? initialsOf(user.name)}
					alt={user.name}
				/>
				<div className="flex min-w-0 flex-grow flex-col gap-0.5">
					<span className="t-row truncate">{user.name}</span>
					{/* `break-all`, а не `truncate`: адрес — первичный идентификатор
					    аккаунта, и обрезанный многоточием он ровно так же
					    нечитаем, как замаскированный. Длинный переносится. */}
					<span className="t-caption break-all text-fg-muted">{user.email}</span>
				</div>
			</div>

			{pointsBalance != null || plan != null ? (
				<SunkenPanel padding="compact" className="justify-between">
					{pointsBalance != null ? (
						<div className="flex flex-col gap-px">
							<span className="t-num-sm">{pointsBalance}</span>
							<span className="t-caption text-fg-muted">{text.pointsUnit}</span>
						</div>
					) : (
						<span />
					)}
					{plan != null ? (
						<StatusBadge progress="done" size="m">
							{plan}
						</StatusBadge>
					) : null}
				</SunkenPanel>
			) : null}

			{manageHref != null ? (
				// Переход — ссылка, а не кнопка с window.location: иначе теряются
				// средний клик, «открыть в новой вкладке» и предпросмотр адреса.
				<a href={manageHref} className={buttonVariants({ variant: "second", fullWidth: true })}>
					<span>{text.manage}</span>
				</a>
			) : onManage != null ? (
				<Button variant="second" fullWidth onClick={onManage}>
					{text.manage}
				</Button>
			) : null}

			{items.length > 0 ? (
				<nav aria-label={text.menu} className="flex flex-col">
					{items.map((item, index) => (
						<MenuEntry key={item.key} entry={item} index={index} />
					))}
				</nav>
			) : null}

			{hasService ? (
				<>
					<Separator />
					<div className="flex flex-col gap-0.5">
						{serviceName != null ? (
							<span className="text-caps-sm px-3 py-1.5 text-fg-muted">{serviceName}</span>
						) : null}
						{serviceItems.map((item, index) => (
							<MenuEntry key={item.key} entry={item} index={index} />
						))}
					</div>
				</>
			) : null}

			{signOutHref != null || onSignOut != null ? (
				<>
					<Separator />
					<MenuEntry
						entry={{
							key: "sign-out",
							label: text.signOut,
							danger: true,
							...(signOutHref !== undefined ? { href: signOutHref } : {}),
							...(onSignOut !== undefined ? { onSelect: onSignOut } : {}),
						}}
						index={0}
					/>
				</>
			) : null}
		</div>
	);
}

function MenuEntry({ entry, index }: { entry: AccountMenuEntry; index: number }) {
	if (entry.href) {
		return (
			<a
				href={entry.href}
				className={cn(
					"iec-press-glyph iec-menu-item flex h-10 w-full items-center gap-2.5 rounded-control px-2.5 t-body",
					"transition-colors duration-150 ease-smooth",
					"focus-ring",
					entry.danger ? "text-danger hover:bg-danger-soft" : "text-fg hover:bg-sunken",
				)}
			>
				{entry.icon}
				<span className="min-w-0 flex-grow truncate">{entry.label}</span>
				{entry.trailing}
			</a>
		);
	}
	return (
		<PopoverItem
			icon={entry.icon}
			danger={entry.danger ?? false}
			index={index}
			trailing={entry.trailing}
			{...(entry.onSelect ? { onClick: entry.onSelect } : {})}
		>
			{entry.label}
		</PopoverItem>
	);
}

export interface AccountMenuPopoverProps extends AccountMenuProps {
	/** Обычно AvatarIcon в шапке сервиса. */
	trigger: ReactNode;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	align?: "start" | "center" | "end";
}

/** Готовая связка «аватар в шапке → меню»: сам поповер, портал и Esc уже внутри. */
export function AccountMenuPopover({
	trigger,
	open,
	onOpenChange,
	align = "end",
	className,
	...menuProps
}: AccountMenuPopoverProps) {
	return (
		<Popover
			trigger={trigger}
			align={align}
			width="auto"
			bare
			{...(open !== undefined ? { open } : {})}
			{...(onOpenChange !== undefined ? { onOpenChange } : {})}
			className="p-0 shadow-none"
			contentLabel={menuProps.labels.menu}
		>
			<AccountMenu {...menuProps} className={className} />
		</Popover>
	);
}
